import React, { useState } from 'react';
import licenseManager from '../utils/licenseManager.js';

/**
 * Modal for entering and redeeming promo codes
 */
const PromoCodeModal = ({ isOpen, onClose }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!code.trim()) {
      setError('Please enter a promo code');
      return;
    }

    setLoading(true);

    try {
      const result = await licenseManager.redeemPromoCode(code);

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          window.location.reload(); // Refresh to show premium features
        }, 2000);
      } else {
        setError(result.error || 'Invalid promo code');
      }
    } catch (err) {
      setError('Failed to redeem code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content promo-code-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>

        <div className="modal-header">
          <h2>🎟️ Redeem Premium Code</h2>
          <p>Enter your promo code to unlock premium features</p>
        </div>

        <form onSubmit={handleSubmit} className="promo-form">
          <div className="input-group">
            <label htmlFor="promo-code">Promo Code</label>
            <input
              id="promo-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="TRAVEL2025"
              autoComplete="off"
              disabled={loading || success}
              className="promo-input"
            />
          </div>

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              ✅ Premium activated successfully!
            </div>
          )}

          <button
            type="submit"
            disabled={loading || success}
            className="btn-primary"
          >
            {loading ? 'Redeeming...' : success ? 'Success!' : 'Redeem Code'}
          </button>
        </form>

        <div className="modal-footer">
          <p className="help-text">
            Don't have a code? Contact support or scan an NFC tag
          </p>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 20px;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          max-width: 500px;
          width: 100%;
          padding: 30px;
          position: relative;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }

        .modal-close {
          position: absolute;
          top: 15px;
          right: 15px;
          background: none;
          border: none;
          font-size: 30px;
          cursor: pointer;
          color: #666;
          line-height: 1;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s;
        }

        .modal-close:hover {
          background: #f0f0f0;
          color: #333;
        }

        .modal-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .modal-header h2 {
          margin: 0 0 10px 0;
          font-size: 24px;
          color: #333;
        }

        .modal-header p {
          margin: 0;
          color: #666;
          font-size: 14px;
        }

        .promo-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .input-group label {
          font-weight: 600;
          color: #333;
          font-size: 14px;
        }

        .promo-input {
          padding: 12px 16px;
          border: 2px solid #ddd;
          border-radius: 8px;
          font-size: 16px;
          font-family: 'Courier New', monospace;
          font-weight: bold;
          letter-spacing: 1px;
          text-align: center;
          transition: all 0.2s;
        }

        .promo-input:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
        }

        .promo-input:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        .error-message {
          background: #fee;
          color: #c33;
          padding: 12px;
          border-radius: 8px;
          font-size: 14px;
          border-left: 4px solid #c33;
        }

        .success-message {
          background: #efe;
          color: #2a2;
          padding: 12px;
          border-radius: 8px;
          font-size: 14px;
          border-left: 4px solid #2a2;
        }

        .btn-primary {
          padding: 14px 24px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .btn-primary:disabled {
          background: #ccc;
          cursor: not-allowed;
          transform: none;
        }

        .modal-footer {
          margin-top: 20px;
          text-align: center;
        }

        .help-text {
          margin: 0;
          font-size: 13px;
          color: #999;
        }
      `}</style>
    </div>
  );
};

export default PromoCodeModal;
